<template>
  <v-container grid-list-xl fluid v-if="!loading">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card class="elevation-1">
          <v-card-title class="headline">
            <v-icon left>mdi-bullhorn-variant-outline</v-icon>
            {{ $t('WHATS_NEW') }}
          </v-card-title>
          <v-divider></v-divider>

          <v-card-text v-if="!releases.length" class="text-center grey--text py-8">
            {{ $t('NO_RELEASES') }}
          </v-card-text>

          <template v-else>
            <div class="px-4 pt-4">
              <v-btn-toggle
                v-model="filter"
                mandatory
                dense
                color="primary"
              >
                <v-btn value="all" small>{{ $t('ALL') }}</v-btn>
                <v-btn value="features" small>
                  <v-icon small color="green darken-2" class="mr-1">mdi-star-circle-outline</v-icon>
                  {{ $t('RELEASE_FEATURES') }}
                </v-btn>
                <v-btn value="improvements" small>
                  <v-icon small color="blue darken-2" class="mr-1">mdi-trending-up</v-icon>
                  {{ $t('RELEASE_IMPROVEMENTS') }}
                </v-btn>
                <v-btn value="fixes" small>
                  <v-icon small color="orange darken-3" class="mr-1">mdi-bug-check-outline</v-icon>
                  {{ $t('RELEASE_FIXES') }}
                </v-btn>
              </v-btn-toggle>
            </div>

            <v-card-text
              v-if="!visibleReleases.length"
              class="text-center grey--text py-8"
            >
              {{ $t('NO_RELEASES') }}
            </v-card-text>

            <v-timeline v-else dense align-top class="pa-4">
              <v-timeline-item
                v-for="release in visibleReleases"
                :key="release.version"
                color="primary"
                large
              >
                <template v-slot:icon>
                  <span class="white--text font-weight-bold release-badge">{{ release.version }}</span>
                </template>

                <v-card class="elevation-1">
                  <v-card-title class="pb-1">
                    <span class="subtitle-1 font-weight-bold">
                      {{ $t('VERSION') }} {{ release.version }}
                      <span v-if="release.title"> — {{ release.title }}</span>
                    </span>
                    <v-spacer></v-spacer>
                    <span class="caption grey--text">{{ formatDate(release.date) }}</span>
                  </v-card-title>

                  <v-card-text class="pt-2">
                    <div v-if="showSection('features', release)" class="mb-3">
                      <div class="font-weight-medium green--text text--darken-2 mb-1">
                        <v-icon small color="green darken-2">mdi-star-circle-outline</v-icon>
                        {{ $t('RELEASE_FEATURES') }}
                      </div>
                      <ul class="release-list">
                        <li v-for="(item, i) in release.features" :key="'f-' + i">{{ item }}</li>
                      </ul>
                    </div>

                    <div v-if="showSection('improvements', release)" class="mb-3">
                      <div class="font-weight-medium blue--text text--darken-2 mb-1">
                        <v-icon small color="blue darken-2">mdi-trending-up</v-icon>
                        {{ $t('RELEASE_IMPROVEMENTS') }}
                      </div>
                      <ul class="release-list">
                        <li v-for="(item, i) in release.improvements" :key="'i-' + i">{{ item }}</li>
                      </ul>
                    </div>

                    <div v-if="showSection('fixes', release)">
                      <div class="font-weight-medium orange--text text--darken-3 mb-1">
                        <v-icon small color="orange darken-3">mdi-bug-check-outline</v-icon>
                        {{ $t('RELEASE_FIXES') }}
                      </div>
                      <ul class="release-list">
                        <li v-for="(item, i) in release.fixes" :key="'x-' + i">{{ item }}</li>
                      </ul>
                    </div>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
  <Loader v-else/>
</template>

<script>
  import { mapActions } from 'vuex';

  import Loader from '../components/shared/Loader';
  import releases from '../assets/releases.json';
  import { getLatestVersion, sortReleasesDesc } from '../utils/version';

  const LAST_SEEN_RELEASE_KEY = 'plas:lastSeenReleaseVersion';

  export default {
    name: 'Dashboard',
    components: { Loader },
    data() {
      return {
        loading: false,
        releases,
        filter: 'all',
      };
    },
    computed: {
      sortedReleases() {
        return sortReleasesDesc(this.releases);
      },
      latestVersion() {
        return getLatestVersion(this.releases);
      },
      visibleReleases() {
        if (this.filter === 'all') return this.sortedReleases;
        return this.sortedReleases.filter((r) => (r[this.filter] || []).length > 0);
      },
    },
    mounted() {
      if (this.latestVersion) {
        window.localStorage.setItem(LAST_SEEN_RELEASE_KEY, String(this.latestVersion));
      }
    },
    methods: {
      formatDate(value) {
        return value ? this.$moment(value).format('DD-MM-YYYY') : '';
      },
      showSection(section, release) {
        const items = release[section] || [];
        if (!items.length) return false;
        return this.filter === 'all' || this.filter === section;
      },
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),
    },
  };
</script>

<style scoped>
  .release-list {
    padding-left: 20px;
    margin: 0;
  }
  .release-badge {
    font-size: 11px;
    letter-spacing: -0.2px;
  }
  .release-list li {
    margin-bottom: 4px;
  }
</style>
